// Primary form coordinator, but it is now concise, easy to read, and free of cluttered text tracking blocks.

/* ==========================================================================
   MODULE 4A: MASTER COORDINATOR LIFECYCLE (consentSubmit.js - PART 1)
   ========================================================================== */
import { validateCustomFormRules } from './formVal';

import { sendEmailJS } from './successEmail';

import { extractCurrentFormDataPayload } from './formExtractor';

import { addTimestampToFooter } from './supportFuncs';

import { 
  cacheRegistrationData, 
  restoreStripeCanceledData, 
  cleanUrlAddressHistory,
  clearRegistrationCache 
} from './formStateCache';

import { renderSummaryCard, handleMasterFormReset } from './summaryRenderer';

// Global runtime email cache dataset allocation table
let emailJSData = {
  parentFNameVal: null, parentLNameVal: null, studentFNameVal: null, studentLNameVal: null,
  studentTypeVal: null, session1Val: null, session2Val: null, session3Val: null, session4Val: null,
  studentLaptopVal: null, parentAttending: null, parent1Dates: null, parent1Laptop: null,
  parent2FName: null, parent2LName: null, parent2Dates: null, parent2Laptop: null,
  parentPhoneVal: null, parentEmailVal: null
};

function updateEmailJSData(payload) {
  emailJSData.parentFNameVal = payload.parentGuardian.parentFName;
  emailJSData.parentLNameVal = payload.parentGuardian.parentLName;
  emailJSData.studentFNameVal = payload.student.studentFName;
  emailJSData.studentLNameVal = payload.student.studentLName;
  emailJSData.studentTypeVal = payload.student.studentType;
  emailJSData.session1Val = payload.student.session1;
  emailJSData.session2Val = payload.student.session2;
  emailJSData.session3Val = payload.student.session3;
  emailJSData.session4Val = payload.student.session4;
  emailJSData.studentLaptopVal = payload.student.studentNeedsLaptop;
  emailJSData.parentAttending = payload.parentGuardian.parentAttending ? 1 : 0;
  emailJSData.parent1Dates = payload.parentGuardian.parent1SessionDates;
  emailJSData.parent1Laptop = payload.parentGuardian.parent1NeedsLaptop;
  emailJSData.parent2FName = payload.parentGuardian.parent2FName;
  emailJSData.parent2LName = payload.parentGuardian.parent2LName;
  emailJSData.parent2Dates = payload.parentGuardian.parent2SessionDates;
  emailJSData.parent2Laptop = payload.parentGuardian.parent2NeedsLaptop;
  emailJSData.parentPhoneVal = payload.parentGuardian.parentPhone;
  emailJSData.parentEmailVal = payload.parentGuardian.parentEmail;
}

function emailJSDataCached(cachedData) {
  emailJSData.parentFNameVal = cachedData.parentGuardian.parentFName;
  emailJSData.parentLNameVal = cachedData.parentGuardian.parentLName;
  emailJSData.studentFNameVal = cachedData.student.studentFName;
  emailJSData.studentLNameVal = cachedData.student.studentLName;
  emailJSData.studentTypeVal = cachedData.student.studentType;
  emailJSData.session1Val = cachedData.student.session1;
  emailJSData.session2Val = cachedData.student.session2;
  emailJSData.session3Val = cachedData.student.session3;
  emailJSData.session4Val = cachedData.student.session4;
  emailJSData.studentLaptopVal = cachedData.student.studentNeedsLaptop;
  emailJSData.parentAttending = cachedData.parentGuardian.parentAttending ? 1 : 0;
  emailJSData.parent1Dates = cachedData.parentGuardian.parent1SessionDates;
  emailJSData.parent1Laptop = cachedData.parentGuardian.parent1NeedsLaptop;
  emailJSData.parent2FName = cachedData.parentGuardian.parent2FName;
  emailJSData.parent2LName = cachedData.parentGuardian.parent2LName;
  emailJSData.parent2Dates = cachedData.parentGuardian.parent2SessionDates;
  emailJSData.parent2Laptop = cachedData.parentGuardian.parent2NeedsLaptop;
  emailJSData.parentPhoneVal = cachedData.parentGuardian.parentPhone;
  emailJSData.parentEmailVal = cachedData.parentGuardian.parentEmail;
}

export function initConsentSubmit(isReturnFromSuccessfulPayment = false, isReturnFromCanceledPayment = false) {

  const gcsRegForm = document.getElementById('reg-form');

  if (!gcsRegForm) return;

  // SCENARIO A: Verified Landing Back From Stripe Checkout Success

  if (isReturnFromSuccessfulPayment) {

    console.log('STRIPE PAYMENT SUCCESSFUL!');

    const cachedData = localStorage.getItem('gcs_summary_cache');

    if (cachedData) {

      console.log("Successful Payment - cachedData: ", cachedData);

      emailJSDataCached(JSON.parse(cachedData));

      console.log("Successful Payment - emailJSData: ", emailJSData);

      renderSummaryCard(JSON.parse(cachedData), emailJSData);
    }

    cleanUrlAddressHistory();
  }

  // SCENARIO B: Stripe Payment Path Aborted or Canceled

  if (isReturnFromCanceledPayment) {

    console.log('STRIPE PAYMENT CANCELED');

    restoreStripeCanceledData();

    cleanUrlAddressHistory();
  }

  // Hook Up Form Reset Actions

  document.getElementById('resetRegFormButton')?.addEventListener('click', (e) => {
    e.preventDefault();
    handleMasterFormReset();
  });

  // Hook Up Printable Action Buttons

  document.getElementById('printRegFormButton')?.addEventListener('click', () => {
    if (window.print) {
      addTimestampToFooter();
      window.print();
    }
  });

/* ==========================================================================
   MODULE 4B: SYSTEM LIFE-CYCLE COORDINATOR (consentSubmit.js - PART 2)
   ========================================================================== */
  // MASTER CAPTURE INTERCEPTOR: Audits validation checks before third-party event listeners fire

  document.querySelectorAll('#payCommitFeeButton, #submitRegFormButton').forEach(btn => {

    btn.addEventListener('click', (e) => {

      console.log(`[Click Interceptor] Pre-auditing input variables for button ID: ${btn.id}`);

      if (!runUnifiedValidationCheck()) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.warn('[Click Interceptor] Intercepted invalid data block. Transition frozen.');
      }
    }, true);
  });

  // FORM SYSTEM LIFE-CYCLE DISPATCHER
  gcsRegForm.addEventListener('submit', (event) => {

    event.preventDefault();

    event.stopPropagation();
    
    if (runUnifiedValidationCheck()) {
      processRegistrationPayload();
    }
  });
}

function runUnifiedValidationCheck() {

  // Ensure these match your literal HTML element IDs perfectly!
  const studentType = document.getElementById('studentType')?.value || '1';
  const parentAttendingBeginner = document.getElementById('parentAttendingBeginner')?.value || 'No';
  const hasAttendanceOption = studentType !== '1';
  const attendanceChoice = hasAttendanceOption ? parentAttendingBeginner : 'No';

  const isCustomRulesValid = validateCustomFormRules({
    studentType,
    attendanceChoice,
    parentFName: document.getElementById('parentFName')?.value || '',
    parentLName: document.getElementById('parentLName')?.value || '',
    parentPhone: document.getElementById('parentPhone')?.value || '',
    
    // VERIFY THESE TWO KEY VALUE STRINGS MATCH YOUR HTML EXACTLY:
    secondParentFName: document.getElementById('secondParentFirstName')?.value || '',
    secondParentLName: document.getElementById('secondParentLastName')?.value || '',
    
    emergFName: document.getElementById('emergFName')?.value || '',
    emergLName: document.getElementById('emergLName')?.value || '',
    emergPhone: document.getElementById('emergPhone')?.value || '',
    
    // VERIFY THESE FOUR MATCH YOUR MARUKUP IDs EXACTLY:
    medIssues: document.getElementById('medIssues')?.value || '',
    explainMed: document.getElementById('explainMed')?.value || '',
    allergies: document.getElementById('allergies')?.value || '',
    explainAllergy: document.getElementById('explainAllergy')?.value || ''
  });

  if (!isCustomRulesValid) return false;

  // FIXED: Standardize checkbox name array selector checking loops
  const checkedSessions = document.querySelectorAll('input[type="checkbox"][id^="session"]:checked').length;

  const feedback = document.getElementById('sessionsFeedback');
  
  if (checkedSessions === 0) {
    if (feedback) feedback.style.display = 'block';
    document.getElementById('form-error-popup').style.display = 'block';
    return false;
  }

  if (feedback) feedback.style.display = 'none';

  return document.getElementById('reg-form').checkValidity();
}

async function processRegistrationPayload() {

  console.log('[Workflow] Form fields validated. Bundling AWS AppSync GraphQL payload...');

  const registrationPayload = extractCurrentFormDataPayload();
  
  // Verify legal signatures match names
  if (registrationPayload.student.studentFName === registrationPayload.parentGuardian.parentFName && !registrationPayload.student.studentSuffix) {
    document.getElementById('same-name-popup').style.display = 'block';
    return;
  }

  if (!registrationPayload.consentAndSignatures.signerName) {
    document.getElementById('signature-popup').style.display = 'block';
    return;
  }

  cacheRegistrationData(registrationPayload);

  updateEmailJSData(registrationPayload);

  // Toggle button animations
  document.querySelectorAll('#payCommitFeeButton, #submitRegFormButton').forEach(btn => {
    btn.disabled = true;
    btn.classList.add('loading');
  });

  document.getElementById('payCommitFeeButton').querySelector('.button-text').textContent = 'Moving to Secure Payment Page...';
  
  document.getElementById('submitRegFormButton').querySelector('.button-text').textContent = 'Processing Your Registration...';


  // SECURE TRANSMISSION TO AWS AMPLIFY APPSYNC MUTATION API

  try {

    const response = await fetch(__APPSYNC_URL__, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': __APPSYNC_KEY__
      },
      body: JSON.stringify({
        query: `mutation ProcessRegistration($registrationPayload: String!, $originUrl: String!) {
          processRegistration(registrationPayload: $registrationPayload, originUrl: $originUrl) { url }
        }`,
        variables: { 
          registrationPayload: JSON.stringify(registrationPayload),
          originUrl: window.location.origin
        }
      }),
    });

    const result = await response.json();

    console.log("First REGISTRATION Lambda Results: ", result);

    if (result.errors) throw new Error('GraphQL Mutation Processing Error');

    const checkoutUrl = result.data?.processRegistration?.url;

    if (!checkoutUrl) {
      // Free tracks transition to local card view
      renderSummaryCard(registrationPayload, emailJSData);
      clearRegistrationCache();
    } else {
      // Beginner student redirects to checkout gateway url
      window.location.href = checkoutUrl;
    }
  } catch (error) {
    console.error('[Workflow] AWS Amplify transaction exception caught:', error);
    document.getElementById('payCommitFeeButton').textContent = 'Pay \$10 Commitment Fee & Complete Registration';
    document.getElementById('submitRegFormButton').textContent = 'Submit Registration Form';
  }
}
